import React, { useEffect } from 'react';

import Layout from '../components/layout';
import SEO from '../components/seo';
import { ScrollToPlugin } from 'gsap/all';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { Statistic } from '../statistics/statistic';
import { StatisticPanel } from '../statistics/statisticPanel';
import gsap from 'gsap';
import headerImage from '../images/banner/banner-3.jpg';
import { isIE } from '../helpers/isIE';
import trainingWelbeing from '../images/stats/training-wellbeing.jpg';

gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);

const KPIs = () => {
    useEffect(() => {
        if (!isIE) {
            //Panel intro animation - late addition. Ideally the panels should be a component but too much refactoring required
            const panels = document.querySelectorAll('.stat-panel, .stat-grid-image > div');
            for (let i = 0; i < panels.length; i++) {
                const element = panels[i];
                gsap.set(element.parentNode, { perspective: 800 });
                gsap.from(element, {
                    scrollTrigger: {
                        trigger: element,
                        start: 'top bottom',
                        // start: 'top bottom-=150px',
                        end: '+=50',
                        scrub: 1.2,
                        once: true,
                    },
                    opacity: 0,
                    rotationX: 90,
                    ease: 'back.out(1.5)',
                });
            }
        }
    }, []);

    return (
        <Layout
            headerTitle="Key Performance Indicators"
            headerIntro="To keep track of how we’re performing, we have indicators which are reported to Government. More detailed information is provided in the Key Performance Indicators section of our Annual Report."
            headerImage={headerImage}
            showCTA={true}
        >
            <SEO title="Key Performance Indicators" />
            <div className="grid-container">
                <div className="o-section o-section--no-space-xs">
                    <div className="uk-grid-medium uk-child-width-1-2@s uk-grid">
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                isDouble
                                value="20%"
                                label="of community members recognise their local natural hazard risks and are prepared to take action."
                                title="Hazard preparedness"
                                description="This means they not only recognise they are at risk of bushfire, storm or cyclone but have also either prepared an emergency kit or written an emergency plan."
                                className="stat--dark-blue stat--white"
                                tableDataPrevYear={['2019/20', '20%', '15%', '+5%']}
                                tableDataCurrentYear={['2020/21', '20%', '20%', '-']}
                            />
                            <Statistic
                                value="82.03%"
                                isDouble
                                label="of scheduled operational courses were delivered this year."
                                title="Operational Courses"
                                description="Our online training system, eAcademy, was upgraded this year and a range of new volunteer training programs were launched. As a direct result, the number of volunteers accessing training this year rose by 23%."
                                className="stat--grey stat--margin"
                                tableDataPrevYear={['2019/20', '72.33%', '80%', '-7.67%']}
                                tableDataCurrentYear={['2020/21', '82.03%', '80%', '+2.03%']}
                            />
                        </div>
                        <Statistic
                            isLarge
                            value="$10.57"
                            label="was the average of cost per home to deliver education and awareness programs and emergency hazard information, nine cents per household more than our target of $10.48."
                            title="Cost to deliver awareness programs"
                            description="Research shows informed communities are better able to manage their risks, reducing the impact of natural hazards and other preventable emergencies. That is why we invest in a range of education and awareness programs able to reach WA’s diverse communities."
                            className="stat--gold"
                            tableDataPrevYear={['2019/20', '$12.16', '$11.39', '+$0.77']}
                            tableDataCurrentYear={['2020/21', '$10.57', '$10.48', '+$0.09']}
                        />
                    </div>
                </div>
            </div>

            <div className="o-section o-section--no-space-xs">
                <div className="stat-panel">
                    <div className="grid-container">
                        <div className="row">
                            <StatisticPanel
                                value="$10.49"
                                label="was the average cost per person to deliver health, safety, wellbeing and training services, $1.81 more than our target of $8.68."
                                title="Cost to deliver training services"
                                description={
                                    <>
                                        <p>
                                            It is essential our people are trained and supported to maintain their physical and mental
                                            health while providing safe and efficient services to the community.
                                            <br />
                                            Our result is higher than budget as a result of additional costs:
                                            <ul>
                                                <li>to develop the Cabinet Submission for the WA Emergency Management Training Centre</li>
                                                <li>for trainee firefighter schools </li>
                                                <li>to formalise firefighter accreditation.</li>
                                            </ul>
                                        </p>
                                    </>
                                }
                                className="stat--dark-blue stat--white"
                                tableDataPrevYear={['2019/20', '$8.90', ' $9.65', '+$0.75']}
                                tableDataCurrentYear={['2020/21', '$10.49', '$8.68', '+$1.81']}
                            />
                            <div className="stat-panel__image">
                                <div style={{ backgroundImage: `url(${trainingWelbeing})` }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid-container">
                <div className="o-section o-section--no-space-xs">
                    <div className="uk-grid-medium uk-child-width-1-2@s uk-grid">
                        <Statistic
                            isLarge
                            value="100%"
                            label="of Level 3 incident Impact Statements are completed and endorsed by the State Recovery Coordinator."
                            title="Impact statements completed"
                            description="Impact Statements allow effective engagement and planning with local government trying to recover from a major incident. In partnering with them, we can ensure the right recovery support is provided to the community."
                            className="stat--grey"
                            tableDataPrevYear={['2019/20', '100%', '80%', '+20%']}
                            tableDataCurrentYear={['2020/21', '100%', '80%', '+20%']}
                        />
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                isDouble
                                value="86.11%"
                                label="of structure fire reports were completed within specified timeframes."
                                title="Structure fire reports completed within timeframes"
                                description={
                                    <>
                                        <p>
                                            When incident reports are completed promptly, it enables buildings to be repaired / rebuilt more
                                            quickly, aiding recovery and reducing the impact on families and businesses.
                                        </p>
                                        <p>
                                            Our result of 86.11% of structure fire incident reports being completed is below target. The
                                            measures we put in place during the year to improve completion rates have not yet delivered the
                                            desired result.
                                        </p>
                                        <p>
                                            The importance of completing incident reports within the specified timeframes will continue to
                                            be emphasised to all responding crews and a review to identify ways to improve our result is
                                            underway.
                                        </p>
                                    </>
                                }
                                className="stat--dark-blue stat--white"
                                tableDataPrevYear={['2019/20', '88.46%', '90%', '-1.54%']}
                                tableDataCurrentYear={['2020/21', '86.11%', '90%', '-3.89%']}
                            />
                            <Statistic
                                value="66.67%"
                                isDouble
                                label="of natural hazard events caused a significant impact on the community."
                                title="Natural hazard events"
                                description={
                                    <>
                                        <p>
                                            Unfortunately, in 2020-21 two of the three Level 3 events (Wooroloo and Tropical Cyclone Seroja)
                                            had a significant impact on the community so we did not meet the target for this indicator.
                                        </p>
                                        <p>
                                            The combination of the catastrophic fire conditions and the topography of the Wooroloo area made
                                            this fire fast moving and difficult to control and 86 homes were lost.
                                        </p>
                                        <p>
                                            Although the community prepared as much as possible before the event, Seroja crossed our coast
                                            in an area where cyclone rated construction codes were not required, resulting in more
                                            structural damage than usually caused by a Category 3 cyclone. Forty-eight homes were lost.
                                        </p>
                                    </>
                                }
                                tableDataPrevYear={['2019/20', '20%', '25%', '+5%']}
                                tableDataCurrentYear={['2020/21', '66.67%', '25%', '-41.67%']}
                                className="stat--gold stat--margin"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="o-section o-section--no-space-xs">
                <div className="stat-panel">
                    <div className="grid-container">
                        <div className="row">
                            <div className="stat-panel__image stat-panel__image--flipped">
                                <div style={{ backgroundImage: `url('/images/stats/ses-cliff-top.jpg')` }}></div>
                            </div>
                            <StatisticPanel
                                isFlipped
                                value="94.03%"
                                label="of responses to ESL 1 and ESL 2 incidents met target times."
                                title="ESL incident target times"
                                description="The result of 94.03% is better than our target of 90% and can largely be attributed to improvements in our reporting systems and processes and efforts of responding crews."
                                tableDataPrevYear={['2019/20', '94.16%', '90%', '+4.16%']}
                                tableDataCurrentYear={['2020/21', '94.03%', '90%', '+4.03%']}
                                className="stat--dark-blue stat--white"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid-container">
                <div className="o-section o-section--no-space-xs">
                    <div className="uk-grid-medium uk-child-width-1-2@s uk-grid">
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                isDouble
                                value="75.24%"
                                label="of structure fires were confined."
                                title="Structure fires confined"
                                description="The effectiveness of our emergency response capability and fire safety education programs meant 936 homes that had a fire were able to be saved this year."
                                tableDataPrevYear={['2019/20', '73.31%', '72%', '+1.31%']}
                                tableDataCurrentYear={['2020/21', '75.24%', '72%', '+3.24%']}
                                className="stat--light"
                            />
                        </div>
                        <div className="uk-flex uk-flex-column">
                            <Statistic
                                value="$164.12"
                                isDouble
                                label="was the average cost to deliver frontline services which is marginally higher than our target of $163.81"
                                title="Average cost for frontline services"
                                description={
                                    <div>
                                        <p style={{ marginTop: '1.5em' }}>
                                            <strong>Frontline (or community facing) services include:</strong>
                                        </p>
                                        <ul className="list-centered">
                                            <li>incident responders</li>
                                            <li>fit-for-purpose response appliances, equipment and facilities</li>
                                            <li>advice to the community and industry</li>
                                            <li>management or mitigation of risks to prevent incidents from occurring</li>
                                        </ul>
                                    </div>
                                }
                                tableDataPrevYear={['2019/20', '$163.27', '$154.85', '+$8.42']}
                                tableDataCurrentYear={['2020/21', '$164.12', '$163.81', '+$0.31']}
                                className="stat--gold"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default KPIs;
